<script setup>
// Imports
import OmniDialogHeader from 'components/UI/Dialogs/OmniDialogHeader.vue'
import { format } from 'quasar'
import { getDialogRecordName } from 'src/utils/dialogRecordName'
import { getDialogIcon, getDialogSubtitle } from 'src/utils/servicePresentation'

// Props
const componentProps = defineProps({
  // New/edit dialogs use the Admin form workspace unless explicitly kept compact.
  formDialog: { type: Boolean, default: null },
  record: { type: Object, default: null },
  recordService: { type: String, default: null },
  icon: { type: String, default: null },
  subtitle: { type: String, default: null },
  itemsService: {
    type: String,
    required: true,
  },
  dialogType: {
    type: String,
    required: true,
  },
  maximized: {
    type: Boolean,
    required: true,
  },
  maximizedToggler: {
    type: Boolean,
    required: true,
  },
  maximizeButton: {
    type: Boolean,
    default: false,
    required: false,
  },
  translationKey: {
    type: String,
    required: false,
    default: null,
  },
  translationOptions: {
    type: Object,
    required: false,
    default: () => {},
  },
  closeBtnDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
})
// Emits
const emit = defineEmits(['update:maximizedToggler'])

// Constants
const { capitalize } = format
const { t, te } = useI18n({ useScope: 'global' })

// Computed
const isAdminFormDialog = computed(
  () => process.env.FRONTEND_TYPE === 'Admin' && (componentProps.formDialog ?? ['new', 'edit'].includes(componentProps.dialogType)),
)
const dialogSubtitle = computed(() => componentProps.subtitle || getDialogSubtitle(componentProps.itemsService, componentProps.dialogType, t, te))
const recordName = computed(() =>
  componentProps.dialogType === 'edit' ? getDialogRecordName(componentProps.recordService || componentProps.itemsService, componentProps.record) : '',
)

const translationPath = computed(() => {
  let translationPath = process.env.FRONTEND_TYPE + '.' + capitalize(componentProps.itemsService)
  if (componentProps.itemsService === 'users') {
    translationPath = 'Shared.Users'
  } else if (componentProps.itemsService === 'globals') {
    translationPath = 'Shared.Globals'
  }

  return translationPath
})

const maximizedToggle = computed({
  get() {
    return componentProps.maximizedToggler
  },
  set(value) {
    emit('update:maximizedToggler', value)
  },
})

// Methods
const maximize = () => {
  if (componentProps.maximizeButton) {
    maximizedToggle.value = !maximizedToggle.value
  }
}
</script>

<template>
  <omni-dialog-header
    :class="{ 'omni-admin-form-header': isAdminFormDialog }"
    :icon="icon || getDialogIcon(itemsService)"
    :subtitle="dialogSubtitle"
    :record-name="recordName"
    @dblclick="maximize"
  >
    <template v-if="$slots.default" #leading><slot /></template>
    {{ translationKey ? $t(translationKey, translationOptions) : $t(translationPath + '.' + capitalize(dialogType) + ' item', translationOptions) }}
    <template #actions>
      <q-btn
        v-if="maximizeButton"
        :ripple="false"
        dense
        flat
        round
        :icon="!maximized ? 'crop_square' : 'fullscreen_exit'"
        :aria-label="$t('Shared.Globals.' + (maximized ? 'Windowed' : 'Maximize'))"
        @click="maximizedToggle = !maximizedToggle"
      >
        <q-tooltip>{{ $t('Shared.Globals.' + (maximized ? 'Windowed' : 'Maximize')) }}</q-tooltip>
      </q-btn>
      <q-btn v-close-popup :disable="closeBtnDisabled" dense flat round icon="close" :aria-label="$t('Shared.Globals.Close')">
        <q-tooltip v-if="!closeBtnDisabled">{{ $t('Shared.Globals.Close') }}</q-tooltip>
      </q-btn>
    </template>
  </omni-dialog-header>
</template>
